import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import { useCreateHoliday, useDeleteHoliday, useEditHoliday, useHolidays } from "hooks/leaveHook"
import { useListParams } from "hooks/useListParams"

const HolidayList = () => {
  document.title = "Holidays"

  const { params, updateParams: setParams } = useListParams({ page: 1, per_page: 15 })
  const [search, setSearch] = useState("")
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear())
  const [modal, setModal] = useState({ open: false, mode: "create", holiday: null })
  const [form, setForm] = useState({ name: "", date: "", description: "" })
  const [deleteId, setDeleteId] = useState(null)

  const { data, isLoading, refetch } = useHolidays({ search, year: yearFilter })
  const holidays = data?.data ?? []
  const createMutation = useCreateHoliday()
  const editMutation = useEditHoliday()
  const deleteMutation = useDeleteHoliday()

  const openCreate = () => {
    setForm({ name: "", date: "", description: "" })
    setModal({ open: true, mode: "create", holiday: null })
  }

  const openEdit = holiday => {
    setForm({ name: holiday.name, date: holiday.date, description: holiday.description || "" })
    setModal({ open: true, mode: "edit", holiday })
  }

  const handleSubmit = async () => {
    try {
      if (modal.mode === "create") {
        await createMutation.mutateAsync(form)
        toast.success("Holiday created")
      } else {
        await editMutation.mutateAsync({ id: modal.holiday.id, ...form })
        toast.success("Holiday updated")
      }
      setModal({ open: false, mode: "create", holiday: null })
      refetch()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed")
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteId)
      toast.success("Holiday deleted")
      setDeleteId(null)
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const columns = useMemo(
    () => [
      { id: "name", Header: "Name", accessor: "name" },
      { id: "date", Header: "Date", accessor: "date" },
      { id: "description", Header: "Description", accessor: "description" },
      {
        id: "actions",
        Header: "Actions",
        accessor: row => (
          <div className="d-flex gap-1">
            <Button size="sm" color="primary" onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Button size="sm" color="danger" onClick={() => setDeleteId(row.id)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Holidays">
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={3}>
              <GlobalInputSearch value={search} onChange={setSearch} placeholder="Search..." />
            </Col>
            <Col md={2}>
              <select className="form-select" value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                {[2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Col>
            <Col className="text-end">
              <Button color="primary" onClick={openCreate}>
                Add Holiday
              </Button>
            </Col>
          </Row>
          <TableContainer columns={columns} data={holidays} isLoading={isLoading} onRefresh={refetch} />
        </CardBody>
      </Card>

      <Modal isOpen={modal.open} toggle={() => setModal({ open: false, mode: "create", holiday: null })}>
        <ModalHeader>{modal.mode === "create" ? "Add" : "Edit"} Holiday</ModalHeader>
        <ModalBody>
          <InputField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
          <InputField label="Date" type="date" value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} />
          <InputField label="Description" type="textarea" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} rows={2} />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={createMutation.isPending || editMutation.isPending}>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModal({ open: false, mode: "create", holiday: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <DeleteModal
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </PageWrapper>
  )
}

export default HolidayList